import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {getMeta} from 'modules/meta/selectors'
import {storeSapiConfig} from 'modules/meta/slice'
import {getLandingQueryString} from 'modules/search/selectors'
import {getHomeUrlWithUTMParams} from 'utils/utmParams'

import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {
  generateSapiLabelFromBrowserContext,
  persistSapiLabel
} from '@vio/core/src/_web/sapiLabel/business'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity
} from '@vio/core/src/analytics/types/Events'
import {calculateTotalRate} from '@vio/core/src/offer/business/calculateTotalRate'
import {initSapiClient} from '@vio/core/src/sapi/services'
import {SearchApiError} from '@vio/core/src/sapi/services/searchApi'

import {type AnySearchError, isAnySearchError} from './errors'
import {sapiLogger} from './sapiLogger'

import type {ClientOptions, ProfileKey, SapiClient} from '@findhotel/sapi'

interface SapiProviderProps {
  profileKey: ProfileKey
  generateOptions: () => ClientOptions
  children: ReactNode
}

interface SapiContextProps {
  sapiClient: SapiClient | undefined
  suggest: SapiClient['suggest']
  searchHotelAvailability: SapiClient['hotelAvailability']
}

/**
 * Handle a (SRP) search error by throwing it in the render thread, triggering the ErrorBoundary
 */
export const useHandleSearchError = () => {
  const dispatch = useDispatch()
  const [requestError, setRequestError] = useState<Error | AnySearchError>(null)
  useEffect(() => {
    if (requestError) {
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Search,
          action: Action.Errored,
          payload: {
            errorMessage: requestError.message,
            sourceComponent: 'SapiProvider',
            ...((requestError as SearchApiError).originalError || {})
          },
          analyticsContext: {
            [AnalyticsContext.ErrorContext]: {
              errorType: 'InvalidSearch',
              errorMessage: requestError.message
            }
          }
        })
      )

      if (isAnySearchError(requestError)) {
        window.location.href = getHomeUrlWithUTMParams()
        return
      }

      throw new SearchApiError(
        'SapiProvider: Error during search',
        requestError
      )
    }
  }, [dispatch, requestError])

  return setRequestError
}

const SapiContext = createContext<Partial<SapiContextProps>>({})

export const SapiProvider = ({
  profileKey,
  generateOptions,
  children
}: SapiProviderProps) => {
  const [sapiClient, setSapiClient] = useState<SapiClient>()
  const dispatch = useDispatch()
  const {brandCode, brandSapiCode} = useBrand()
  const {search} = useBrandCapabilities()
  const {includeTaxes, includeLocalTaxes} = useSelector(getMeta)
  const landingQueryString = useSelector(getLandingQueryString)

  // Store the traffic source in a cookie for 10 minutes so RSP can use it
  useEffect(() => {
    const sapiLabel = generateSapiLabelFromBrowserContext(
      landingQueryString,
      !search.skipLabelPersistence && (brandSapiCode || brandCode)
    )
    if (sapiLabel) {
      persistSapiLabel(sapiLabel)
    }
  }, [
    landingQueryString,
    brandCode,
    search.skipLabelPersistence,
    brandSapiCode
  ])

  useEffect(() => {
    const options = generateOptions()

    async function createSapiClient() {
      try {
        const client = await initSapiClient(profileKey, {
          ...options,
          getTotalRate: rate =>
            calculateTotalRate(rate, includeTaxes, includeLocalTaxes),
          logger: sapiLogger,
          callbacks: {
            onConfigReceived: configs => {
              dispatch(storeSapiConfig(configs))
            }
          }
        })
        setSapiClient(client)
      } catch (error) {
        if (typeof Sentry !== 'undefined') {
          Sentry.captureException(error)
        }

        dispatch(
          trackEvent({
            category: Category.System,
            entity: Entity.Search,
            action: Action.Errored,
            analyticsContext: {
              [AnalyticsContext.ErrorContext]: {
                errorType: 'SapiInitializationError',
                errorMessage: error.message
              }
            }
          })
        )

        setSapiClient(undefined)
      }
    }

    createSapiClient()
  }, [])

  const searchHotelAvailability = useCallback(
    async (...args: Parameters<SapiClient['hotelAvailability']>) => {
      if (sapiClient !== undefined) {
        try {
          const results = await sapiClient.hotelAvailability(...args)
          return results
        } catch (e) {
          // Temporary solution until we have a proper error handling callback
          const callbacks = args[1]
          const {onComplete} = callbacks
          onComplete({
            availability: {}
          })
        }
      }
    },
    [sapiClient]
  )

  return (
    <SapiContext.Provider
      value={{
        sapiClient,
        // Added as part of 6beacd2c-perform-two-searches
        searchHotelAvailability
      }}
    >
      {children}
    </SapiContext.Provider>
  )
}

export function useSapi() {
  const {sapiClient, searchHotelAvailability} = useContext(SapiContext)

  return {
    suggest: sapiClient?.suggest,
    sapiClient,
    searchHotelAvailability
  }
}
